import React from 'react';
import { StudyPlanSettingsFormProps, StudyPlanSettingsFormValues } from './types';
import style from './style.module.scss';
import { SecondaryButton } from '@magoosh/lib/button';
import { Formik, FormikHelpers as FormikActions } from 'formik';
import { Form } from 'react-bootstrap';
import RadioGroup from '@magoosh/lib/formik_inputs/field_radio_group';
import * as yup from 'yup';
import FieldButtonGroup from '@magoosh/lib/formik_inputs/field_button_group';
import capitalize from 'react-bootstrap/lib/utils/capitalize';
import { uniqBy } from 'lodash';
import { pluralize } from 'utilities/pluralize';

const validationSchema = yup.object({
  mathAndVerbalMix: yup.string().when('planType', {
    is: 'adaptive',
    then: yup.string().required()
  }),
  shouldIncludeWriting: yup.string().when('planType', {
    is: 'adaptive',
    then: yup.string().required()
  }),
  numMonths: yup.number().when('planType', {
    is: 'preset',
    then: yup.number().required()
  }),
  experienceLevel: yup.string().when('planType', {
    is: 'preset',
    then: yup.string().required()
  })
});

export const StudyPlanSettingsForm: React.FC<StudyPlanSettingsFormProps> = (props) => {
  const { planType, currentSettings, presetPlanOptions, onClose, onSubmit } = props;

  const getUniqueMonths = () => {
    const uniqueOptions = uniqBy(presetPlanOptions, (e) => {
      return e.numMonths;
    });

    return uniqueOptions.map((option) => {
      return {
        label: `${pluralize(option.numMonths, 'Month')}`,
        value: option.numMonths
      };
    });
  };

  const getUniqueExperienceLevels = () => {
    const uniqueOptions = uniqBy(presetPlanOptions, (e) => {
      return e.experienceLevel;
    });

    return uniqueOptions.map((option) => {
      return {
        label: `${capitalize(option.experienceLevel)}`,
        value: option.experienceLevel,
        hint: option.description
      };
    });
  };

  const findPresetPlanContentBlockId = (numMonths: number, experienceLevel: string): number => {
    const selectedPlan = presetPlanOptions.find((option) => {
      return option.numMonths == numMonths && option.experienceLevel == experienceLevel;
    });
    return selectedPlan.id;
  };

  const handleOnSubmit = (
    values: StudyPlanSettingsFormValues,
    { resetForm }: FormikActions<StudyPlanSettingsFormValues>
  ): void => {
    if (values.planType == 'preset') {
      onSubmit({
        planType: values.planType,
        presetPlanContentBlockId: findPresetPlanContentBlockId(values.numMonths, values.experienceLevel)
      });
    } else {
      onSubmit({
        planType: values.planType,
        mathAndVerbalMix: values.mathAndVerbalMix,
        shouldIncludeWriting: values.shouldIncludeWriting === 'yes'
      });
    }

    onClose();
    resetForm({ values });
  };

  const initialValues = {
    planType: planType ? planType : 'adaptive',
    mathAndVerbalMix: currentSettings.mathAndVerbalMix,
    shouldIncludeWriting:
      currentSettings.shouldIncludeWriting === undefined
        ? 'yes'
        : currentSettings.shouldIncludeWriting
        ? 'yes'
        : 'no',
    numMonths: currentSettings.numMonths,
    experienceLevel: currentSettings.experienceLevel
  };

  const handleOnClose = (resetForm): void => {
    resetForm({ values: initialValues });
    onClose();
  };

  return (
    <div className={style.studyPlanSettingsForm}>
      <Formik initialValues={initialValues} onSubmit={handleOnSubmit} validationSchema={validationSchema}>
        {({
          dirty,
          isSubmitting,
          values,
          setFieldValue,
          setFieldTouched,
          submitForm,
          resetForm,
          isValid
        }) => (
          <Form>
            <h4 className="u-margin-B-n">Type</h4>
            <FieldButtonGroup
              name="planType"
              variant="dark"
              options={[
                {
                  displayName: 'Adaptive',
                  value: 'adaptive'
                },
                {
                  displayName: 'Preset',
                  value: 'preset'
                }
              ]}
              onChangeCondition={(newValue) => {
                if (newValue === initialValues.planType) {
                  resetForm({ values: initialValues });
                }
              }}
            />

            {values.planType === 'adaptive' ? (
              <p>
                Offers <strong>personalized priorities</strong> based on your performance and a{' '}
                <strong>flexible daily view.</strong>
              </p>
            ) : (
              <p>
                Offers a <strong>classic study plan</strong> organized across a{' '}
                <strong>fixed number of days.</strong>
              </p>
            )}
            <hr className="u-margin-A-n u-margin-B-xs" />

            {values.planType === 'adaptive' && (
              <div>
                <p className="u-margin-B-n">
                  <strong>Math and Verbal Section Mix</strong>
                </p>

                <RadioGroup
                  name="mathAndVerbalMix"
                  className="u-margin-A-n"
                  optionsArray={[
                    {
                      label: 'Balanced',
                      value: 'balanced',
                      labelTag: 'Popular'
                    },
                    {
                      label: 'More Math, Less Verbal',
                      value: 'more_math'
                    },
                    {
                      label: 'More Verbal, Less Math',
                      value: 'more_verbal'
                    },
                    {
                      label: 'Only Math',
                      value: 'only_math'
                    },
                    {
                      label: 'Only Verbal',
                      value: 'only_verbal'
                    }
                  ]}
                  value={values.mathAndVerbalMix}
                />

                <hr className="u-margin-A-n u-margin-B-xs" />

                <p className="u-margin-B-n">
                  <strong>Writing Topics</strong>
                </p>

                <RadioGroup
                  name="shouldIncludeWriting"
                  optionsArray={[
                    {
                      label: 'Include',
                      value: 'yes'
                    },
                    {
                      label: 'Exclude',
                      value: 'no'
                    }
                  ]}
                  value={values.shouldIncludeWriting}
                />
              </div>
            )}

            {values.planType === 'preset' && (
              <div>
                <p className="u-margin-B-n">
                  <strong>Timeframe</strong>
                </p>

                <RadioGroup
                  name="numMonths"
                  className="u-margin-A-n"
                  optionsArray={getUniqueMonths()}
                  value={values.numMonths}
                  onChangeCondition={() => {
                    if (values.experienceLevel === undefined) {
                      setFieldValue('experienceLevel', getUniqueExperienceLevels()[0].value);
                      setTimeout(() => setFieldTouched('experienceLevel', true));
                    }
                  }}
                />

                <hr className="u-margin-A-n u-margin-B-xs" />

                <p className="u-margin-B-n">
                  <strong>Level</strong>
                </p>

                <RadioGroup
                  name="experienceLevel"
                  className="u-margin-A-n"
                  optionsArray={getUniqueExperienceLevels()}
                  value={values.experienceLevel}
                  onChangeCondition={() => {
                    if (values.numMonths === undefined) {
                      setFieldValue('numMonths', getUniqueMonths()[0].value);
                      setTimeout(() => setFieldTouched('numMonths', true));
                    }
                  }}
                />
              </div>
            )}

            <hr className="u-margin-A-n u-margin-B-xs" />

            <SecondaryButton disabled={!dirty || isSubmitting || !isValid} onClick={submitForm}>
              Save and Modify
            </SecondaryButton>
            <a className={style.cancelButton} onClick={() => handleOnClose(resetForm)}>
              Cancel
            </a>
            <p className="u-margin-T-xs">(Your study progress will be carried over.)</p>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default StudyPlanSettingsForm;
